import { useEffect, useMemo, useState } from "react";
import { useToys } from "../Toy/ToysContext";
import ToyCard from "../Toy/ToyCard";

const FILTER_LABELS = {
  all: "All",
  underwater: "🤿 Underwater",
  efoil: "⚡ E-Foil",
  surf: "🏄‍♂️ Surf",
  inflatable: "🛟 Inflatables",
  kids: "🧸 Kids",
  pool: "🏝️ Pools",
  platforms: "🧘‍♂️ Platforms",
};

/** Preferred chip order when those slugs exist; remaining Airtable slugs follow alphabetically. */
const LEGACY_FILTER_ORDER = [
  "underwater",
  "efoil",
  "surf",
  "inflatable",
  "kids",
  "pool",
  "platforms",
];

function formatSlugLabel(slug) {
  return slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getFilterLabel(filterKey) {
  if (FILTER_LABELS[filterKey]) return FILTER_LABELS[filterKey];
  return formatSlugLabel(filterKey);
}

export default function ToysGrid() {
  const {
    toys,
    reservationDate,
    availabilityByToyId,
    isLoadingAvailability,
  } = useToys();
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    if (activeFilter === "water") setActiveFilter("all");
  }, [activeFilter]);

  const availableFilters = useMemo(() => {
    const used = new Set();
    toys.forEach((toy) => {
      (toy.filters || []).forEach((filter) => used.add(filter));
    });
    const legacy = LEGACY_FILTER_ORDER.filter((slug) => used.has(slug));
    const rest = [...used]
      .filter((slug) => !LEGACY_FILTER_ORDER.includes(slug))
      .sort((a, b) => a.localeCompare(b));
    return ["all", ...legacy, ...rest];
  }, [toys]);

  const filteredToys = useMemo(() => {
    if (activeFilter === "all") return toys;
    return toys.filter((toy) => (toy.filters || []).includes(activeFilter));
  }, [activeFilter, toys]);

  const displayToys = useMemo(() => {
    return filteredToys.filter((toy) => {
      if (!reservationDate || isLoadingAvailability) return true;
      if (
        !Object.prototype.hasOwnProperty.call(availabilityByToyId, toy.id)
      ) {
        return true;
      }
      return availabilityByToyId[toy.id] > 0;
    });
  }, [
    filteredToys,
    reservationDate,
    isLoadingAvailability,
    availabilityByToyId,
  ]);

  return (
    <section>
      <div className="controls-strip" aria-label="Category filters">
        <div className="controls">
          {availableFilters.map((filterKey) => (
            <button
              key={filterKey}
              type="button"
              className={`chip ${activeFilter === filterKey ? "active" : ""}`}
              onClick={() => setActiveFilter(filterKey)}
            >
              {getFilterLabel(filterKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="sec-head">
        <h2 className="sec-title">Available Equipment</h2>
        <span className="sec-count">
          {displayToys.length} item{displayToys.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="grid">
        {displayToys.map((toy) => (
          <ToyCard key={toy.id} toy={toy} />
        ))}
      </div>
    </section>
  );
}
